import { useCallback, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { JSONContent } from '@tiptap/core';
import type {
  WorkplaceColorSpectrumBlock,
  WorkplaceSpectrumTitleLevel,
  SpectrumColor,
} from '@course-authoring/shared-types';
import {
  richTextToPlainText,
  useTheme,
} from '@course-authoring/shared-types';
import { useIsMobileViewport } from '@course-authoring/ui-blocks';

import { InteractionRichTextField } from './InteractionRichTextField';
import {
  createTokenRichTextToolbarStyles,
  EMPTY_RICH_TEXT_DOC,
} from './interactionEditorUtils';
import { TitleLevelSelector, getTitleInputStyle } from './TitleLevelSelector';
import { QuestionSelector } from './questionBank/QuestionSelector';
import {
  ManagedInBankNote,
  createReadOnlyTextStyle,
  resolveQuestionEditorMode,
} from './questionBank/questionEditorShared';
import { useQuestionTextOverride } from './questionBank/useQuestionTextOverride';
import { FieldOverrideStatus } from './questionBank/FieldOverrideStatus';
import { useFieldOverrideState } from './questionBank/useFieldOverrideState';

export interface WorkplaceColorSpectrumEditorProps {
  block: WorkplaceColorSpectrumBlock;
  onChange: (updates: Partial<WorkplaceColorSpectrumBlock>) => void;
  autoFocus?: boolean;
  /**
   * 'block' (default): lesson-block mode — title controls + QuestionSelector,
   * question content rendered read-only from the resolved block fields.
   * 'questionModal': create-question modal mode — editable question fields,
   * no title controls, no lock toggles.
   */
  variant?: 'block' | 'questionModal';
  translationMode?: boolean;
}

type FieldName =
  | 'title'
  | 'description'
  | 'correctAnswer'
  | 'correctAnswerExplanation';

const COLOR_KEYS: SpectrumColor[] = ['green', 'yellow', 'orange', 'red'];

// Mirrors tokens.wcs.* (design-system Batch 3.4 + 1.4 + 1.5).
// Must stay in sync with themeTokens.ts and WorkplaceColorSpectrumRenderer.
const COLOR_LABELS: Record<SpectrumColor, { name: string; hex: string }> = {
  green:  { name: 'Green',  hex: '#6FBE55' }, // tokens.wcs.green
  yellow: { name: 'Yellow', hex: '#FFED0F' }, // tokens.wcs.yellow
  orange: { name: 'Orange', hex: '#F9861C' }, // tokens.wcs.orange
  red:    { name: 'Red',    hex: '#ED1E63' }, // tokens.wcs.red
};

const editorInnerStyles = `
  .spectrum-editor p {
    margin: 0;
  }
  .spectrum-editor p:not(:last-child) {
    margin-bottom: 0.5em;
  }
`;

// --- Main editor ---

export function WorkplaceColorSpectrumEditor({
  block,
  onChange,
  autoFocus = false,
  variant = 'block',
  translationMode = false,
}: WorkplaceColorSpectrumEditorProps): React.ReactElement {
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedField, setFocusedField] = useState<FieldName | null>(null);

  const { isModal, isManaged, showBlockAuthoringControls } = resolveQuestionEditorMode(
    variant,
    block,
    translationMode,
  );

  const titleLevel: WorkplaceSpectrumTitleLevel = block.titleLevel ?? 'h2';
  const correctAnswer: SpectrumColor = block.correctAnswer ?? 'yellow';
  const interactionWidth = isMobile
    ? '90%'
    : '80%';

  const handleFieldFocus = useCallback((field: FieldName) => {
    setFocusedField(field);
  }, []);

  const handleFieldBlur = useCallback(() => {
    setFocusedField(null);
  }, []);

  // Still needed for the read-only translation-mode display below.
  const descriptionText = richTextToPlainText(block.description);

  // NA-382: question text is authored on the block (or on the bank value in the
  // modal); see useQuestionTextOverride for why the commit is on blur.
  const questionText = useQuestionTextOverride(block, onChange, isModal, translationMode);

  // NA-313: the correct-answer explanation is the one bank-owned field this
  // block may take ownership of. Same rules as the Single Choice editor, with
  // this type's own key names passed in.
  const explanationOverride = useFieldOverrideState(block, {
    value: 'correctAnswerExplanation',
    marker: 'correctAnswerExplanationOverridden',
    bankValue: 'correctAnswerExplanationBankValue',
  });

  const handleCorrectAnswerExplanationChange = useCallback(
    (content: JSONContent) => onChange(explanationOverride.buildEdit(content)),
    [explanationOverride, onChange],
  );

  const handleExplanationRevert = useCallback(
    () => onChange(explanationOverride.buildRevert()),
    [explanationOverride, onChange],
  );

  // NA-384: title, same override model, plain string instead of rich text.
  // Ownership is only ever claimed here in normal English editing while the
  // bank actually has a title to diverge from — a translation write must
  // never be able to claim or release it (see `lesson-block-translation`),
  // and with no question selected or no bank title at all there is nothing
  // to own, so all three cases fall back to the plain field update that
  // already existed before this capability. `titleBankValue` (not just
  // `questionDefinitionId`) is the "bank has a title" check specifically
  // because `dtoToBlockFields` seeds it at selection time — reliable even
  // before the next server round-trip.
  const titleOverride = useFieldOverrideState<WorkplaceColorSpectrumBlock, string>(block, {
    value: 'title',
    marker: 'titleOverridden',
    bankValue: 'titleBankValue',
    requireBankValue: true,
  });
  const hasBankTitle = block.titleBankValue !== undefined;

  const handleTitleChange = useCallback(
    (next: string) => onChange(
      hasBankTitle && !translationMode ? titleOverride.buildEdit(next) : { title: next },
    ),
    [hasBankTitle, translationMode, titleOverride, onChange],
  );

  const handleTitleRevert = useCallback(
    () => onChange(titleOverride.buildRevert()),
    [titleOverride, onChange],
  );

  const richTextToolbarStyles = useMemo(
    () => createTokenRichTextToolbarStyles(tokens),
    [tokens]
  );

  const styles = useMemo((): Record<string, CSSProperties> => ({
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
      ...(isModal ? {} : { width: interactionWidth, margin: '0 auto' }),
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
    header: {
      marginBottom: tokens.spacing.sm,
    },
    titleInput: {
      ...getTitleInputStyle(titleLevel, tokens),
      marginBottom: tokens.spacing.sm,
    },
    readOnlyText: createReadOnlyTextStyle(tokens),
    descriptionEditor: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.muted,
      lineHeight: tokens.typography.body.lineHeight,
    },
    sectionTitleRow: {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: tokens.spacing.sm,
      marginBottom: tokens.spacing.sm,
    },
    sectionTitleText: {
      fontSize: '13px',
      fontWeight: 700,
      color: tokens.text.primary,
    },
    fieldLabel: {
      fontSize: '12px',
      fontWeight: 600,
      color: tokens.text.muted,
      marginBottom: tokens.spacing.xs,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    fieldWrapper: {
      marginBottom: tokens.spacing.md,
    },
    answerSettingsSection: {
      backgroundColor: tokens.backgrounds.app,
      borderRadius: tokens.radius.md,
      padding: tokens.spacing.lg,
      border: `1px solid ${tokens.borders.default}`,
      boxSizing: 'border-box',
    },
    selectInput: {
      width: '100%',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      fontWeight: 500,
      color: tokens.text.primary,
      outline: 'none',
      backgroundColor: tokens.backgrounds.app,
      boxSizing: 'border-box',
      appearance: 'none',
      cursor: isModal ? 'pointer' : 'default',
    },
    colorDescriptionEditor: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.primary,
      lineHeight: tokens.typography.body.lineHeight,
    },
    colorsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
    },
  }), [interactionWidth, isModal, titleLevel, tokens]);

  return (
    <div ref={containerRef} style={styles.container}>
      <style>{editorInnerStyles}</style>

      {!isModal && (
        <div style={styles.header}>
          {showBlockAuthoringControls && (
            <TitleLevelSelector
              currentLevel={titleLevel}
              onSelectLevel={(level) => onChange({ titleLevel: level as WorkplaceSpectrumTitleLevel })}
              tokens={tokens}
            />
          )}

          <input
            type="text"
            value={titleOverride.displayValue ?? ''}
            placeholder="Enter title..."
            onChange={(e) => handleTitleChange(e.target.value)}
            onFocus={() => handleFieldFocus('title')}
            onBlur={handleFieldBlur}
            style={styles.titleInput}
            autoFocus={autoFocus}
          />
          {!translationMode && (
            <FieldOverrideStatus
              isDiverged={titleOverride.isDiverged}
              onRevert={handleTitleRevert}
              fieldLabel="title"
            />
          )}

          {showBlockAuthoringControls && (
            <QuestionSelector
              block={block}
              onChange={onChange as (updates: Record<string, unknown>) => void}
            />
          )}
        </div>
      )}

      {(isModal || isManaged) && (
        <>
      {/*
        Question text.

        NA-382: block-overridable on all four bank-backed types, so it is editable
        in BOTH the create-question modal (authoring the bank value) and the
        lesson block (overriding it) — hence no `isModal` gate and no managed note
        on this card, for the same reason NA-313 removed one from the explanation.
        The correct answer below stays read-only and managed. It was previously
        read-only PLAIN TEXT in block mode, so rich formatting becomes visible
        here for the first time.
      */}
      {questionText.isEditable ? (
      <div style={isModal ? undefined : styles.answerSettingsSection}>
        {isModal ? (
          <div style={styles.fieldLabel}>Question text</div>
        ) : (
          <div style={styles.sectionTitleRow}>
            <span style={styles.sectionTitleText}>Question text</span>
            {questionText.showStatus && (
              <FieldOverrideStatus
                isDiverged={questionText.override.isDiverged}
                onRevert={questionText.onRevert}
                fieldLabel="question text"
              />
            )}
          </div>
        )}
        <InteractionRichTextField
          content={questionText.content ?? EMPTY_RICH_TEXT_DOC}
          placeholder="Enter question text..."
          isFocused={focusedField === 'description'}
          onFocus={() => handleFieldFocus('description')}
          onBlur={handleFieldBlur}
          onContentChange={questionText.onContentChange}
          autoFocus={autoFocus}
          containerRef={containerRef}
          editorClassName="tiptap spectrum-editor"
          editorStyle={styles.descriptionEditor}
          toolbarPreset="full"
          toolbarStyles={richTextToolbarStyles}
        />
      </div>
      ) : descriptionText !== '' ? (
        /*
          Translation mode, question text NOT overridden: the field stays Question
          Bank-owned, so it is shown as read-only localized context rather than as
          an editable block field (`lesson-block-translation`). Editing it here
          would let a translation claim ownership, which the default-locale scope
          alone may do.
        */
        <div style={styles.answerSettingsSection}>
          <div style={styles.sectionTitleRow}>
            <span style={styles.sectionTitleText}>Question text</span>
            <ManagedInBankNote when={isManaged} />
          </div>
          <div style={styles.readOnlyText}>{descriptionText}</div>
        </div>
      ) : null}

      <div style={styles.colorsList}>
        {/*
          NA-313 split this from one "Results settings" section into two.
          A single section-level "Managed in Question Bank" note used to cover
          both the correct answer (still bank-owned) and the explanation (now
          block-overridable), so leaving it would assert something false about
          the explanation. Splitting keeps each marker literally true rather than
          moving the note to field level and leaving a mixed section.
        */}
        <div style={styles.answerSettingsSection}>
          <div style={styles.sectionTitleRow}>
            <span style={styles.sectionTitleText}>Results settings</span>
            <ManagedInBankNote when={isManaged} />
          </div>

          <div style={styles.fieldWrapper}>
            <div style={styles.fieldLabel}>Correct answer</div>
            <select
              aria-label="Correct answer"
              value={correctAnswer}
              onChange={(e) => onChange({ correctAnswer: e.target.value as SpectrumColor })}
              onFocus={() => handleFieldFocus('correctAnswer')}
              onBlur={handleFieldBlur}
              style={styles.selectInput}
              disabled={!isModal}
            >
              {COLOR_KEYS.map((color) => (
                <option key={color} value={color}>
                  {COLOR_LABELS[color].name}
                </option>
              ))}
            </select>
          </div>

        </div>

        {/*
          Block-overridable, so it gets its own section with no managed note —
          editable in the create-question modal (authoring the bank value) and on
          the lesson block (overriding it).
        */}
        <div style={styles.answerSettingsSection}>
          <div style={styles.sectionTitleRow}>
            <span style={styles.sectionTitleText}>Correct answer explanation</span>
            {!isModal && !translationMode && (
              <FieldOverrideStatus
                isDiverged={explanationOverride.isDiverged}
                onRevert={handleExplanationRevert}
                fieldLabel="correct answer explanation"
              />
            )}
          </div>

          <div>
            <InteractionRichTextField
              content={explanationOverride.displayValue ?? EMPTY_RICH_TEXT_DOC}
              placeholder="Explain why this is the correct answer..."
              isFocused={focusedField === 'correctAnswerExplanation'}
              onFocus={() => handleFieldFocus('correctAnswerExplanation')}
              onBlur={handleFieldBlur}
              onContentChange={handleCorrectAnswerExplanationChange}
              autoFocus={false}
              containerRef={containerRef}
              editorClassName="tiptap spectrum-editor"
              editorStyle={styles.colorDescriptionEditor}
              toolbarPreset="full"
              toolbarStyles={richTextToolbarStyles}
            />
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
}

WorkplaceColorSpectrumEditor.displayName = 'WorkplaceColorSpectrumEditor';
